import React, { useEffect, useState } from "react";
import { HTTPURL } from "../../../constants/Matcher";
import { C } from "../../../constants/mq";

// Enquiry form, used on the home page and the contact page.
//
// Replaces GetInTouch, which posted to api/users/contact-us on a hardcoded
// IP (65.1.219.190) that no longer resolves — and that route never existed
// on our API either, so submissions went nowhere and the visitor was told
// nothing. This posts to api/public/contact and reports what happened.

const BRAND = C.red;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const s = {
  card: {
    background: "#ffffff",
    border: `1px solid ${C.line}`,
    borderRadius: 4,
    boxShadow: "0 1px 3px rgba(0,0,0,0.07)",
    padding: "30px 30px 34px",
  },
  eyebrow: {
    display: "inline-block",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "1.5px",
    textTransform: "uppercase",
    color: BRAND,
    margin: "0 0 8px",
  },
  heading: {
    fontSize: 26,
    fontWeight: 800,
    color: C.ink,
    margin: "0 0 6px",
    letterSpacing: "-0.3px",
  },
  sub: { fontSize: 14.5, color: C.body, margin: "0 0 24px", lineHeight: 1.6 },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 16,
  },
  field: { display: "flex", flexDirection: "column", gap: 6 },
  label: { fontSize: 12.5, fontWeight: 600, color: C.ink },
  input: {
    height: 48,
    borderRadius: 4,
    border: "1px solid #c9c6bd",
    padding: "0 14px",
    fontSize: 15,
    color: C.ink,
    background: "#fff",
    outline: "none",
    width: "100%",
    fontFamily: "inherit",
  },
  textarea: {
    minHeight: 120,
    borderRadius: 4,
    border: "1px solid #c9c6bd",
    padding: "12px 14px",
    fontSize: 15,
    color: C.ink,
    background: "#fff",
    outline: "none",
    width: "100%",
    resize: "vertical",
    fontFamily: "inherit",
  },
  full: { gridColumn: "1 / -1" },
  err: { fontSize: 12, color: BRAND, minHeight: 16 },
  actions: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    marginTop: 20,
    flexWrap: "wrap",
  },
  submit: {
    background: BRAND,
    color: "#fff",
    border: "none",
    borderRadius: 4,
    minHeight: 50,
    padding: "0 34px",
    fontSize: 14.5,
    fontWeight: 600,
    cursor: "pointer",
    boxShadow: "0 2px 4px rgba(0,0,0,0.18)",
  },
  submitOff: { opacity: 0.6, cursor: "not-allowed" },
  ok: {
    background: "#ecfdf5",
    border: "1px solid #a7f3d0",
    color: "#065f46",
    borderRadius: 10,
    padding: "16px 18px",
    fontSize: 14.5,
    lineHeight: 1.6,
  },
  failed: {
    background: "#fef2f2",
    border: "1px solid #fecaca",
    color: "#991b1b",
    borderRadius: 10,
    padding: "12px 16px",
    fontSize: 14,
    marginTop: 16,
  },
};

const blank = {
  name: "",
  email: "",
  phone: "",
  branch_id: "",
  grade_id: "",
  message: "",
};

const ContactForm = ({
  eyebrow = "Need help?",
  heading = "Get in touch",
  sub = "Tell us your child's year level and what you are after, and we will point you to the right program.",
}) => {
  const [values, setValues] = useState(blank);
  const [errors, setErrors] = useState({});
  const [branches, setBranches] = useState([]);
  const [grades, setGrades] = useState([]);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [failed, setFailed] = useState("");

  useEffect(() => {
    let alive = true;
    const load = async (path, set, pick) => {
      try {
        const r = await fetch(`${HTTPURL}${path}`);
        const j = await r.json();
        if (alive) set(pick(j?.data || []));
      } catch {
        /* the selects are optional — a failure just leaves them empty */
      }
    };
    load("api/branches-all", setBranches, (d) => d);
    load("api/grades-all", setGrades, (d) => d);
    return () => {
      alive = false;
    };
  }, []);

  const set = (k) => (e) => {
    setValues((v) => ({ ...v, [k]: e.target.value }));
    setErrors((x) => ({ ...x, [k]: "" }));
  };

  const validate = () => {
    const next = {};
    if (!values.name.trim()) next.name = "Please enter your name";
    if (!EMAIL_RE.test(values.email.trim()))
      next.email = "Please enter a valid email address";
    if (!values.message.trim()) next.message = "Please tell us how we can help";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setFailed("");
    if (!validate() || sending) return;
    setSending(true);
    try {
      const res = await fetch(`${HTTPURL}api/public/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      });
      const json = await res.json();
      if (json?.status) {
        setDone(true);
        setValues(blank);
      } else {
        setFailed(json?.message || "Something went wrong. Please try again.");
      }
    } catch {
      setFailed(
        "We could not send that just now. Please try again, or call us on +61 2 8883 0055."
      );
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div style={s.card}>
        <span style={s.eyebrow}>Thank you</span>
        <h2 style={s.heading}>Your message is on its way</h2>
        <div style={s.ok}>
          We have passed your enquiry to the right campus and someone will get
          back to you shortly. If it is urgent, call us on +61 2 8883 0055.
        </div>
        <div style={s.actions}>
          <button type="button" style={s.submit} onClick={() => setDone(false)}>
            Send another message
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={s.card}>
      <span style={s.eyebrow}>{eyebrow}</span>
      <h2 style={s.heading}>{heading}</h2>
      <p style={s.sub}>{sub}</p>

      <form onSubmit={onSubmit} noValidate>
        <div style={s.grid}>
          <div style={s.field}>
            <label style={s.label} htmlFor="cf-name">
              Full name *
            </label>
            <input
              id="cf-name"
              style={s.input}
              value={values.name}
              onChange={set("name")}
              placeholder="Your name"
            />
            <span style={s.err}>{errors.name}</span>
          </div>

          <div style={s.field}>
            <label style={s.label} htmlFor="cf-email">
              Email *
            </label>
            <input
              id="cf-email"
              type="email"
              style={s.input}
              value={values.email}
              onChange={set("email")}
              placeholder="you@example.com"
            />
            <span style={s.err}>{errors.email}</span>
          </div>

          <div style={s.field}>
            <label style={s.label} htmlFor="cf-phone">
              Phone
            </label>
            <input
              id="cf-phone"
              style={s.input}
              value={values.phone}
              onChange={set("phone")}
              placeholder="Optional"
            />
            <span style={s.err} />
          </div>

          <div style={s.field}>
            <label style={s.label} htmlFor="cf-branch">
              Campus
            </label>
            <select
              id="cf-branch"
              style={s.input}
              value={values.branch_id}
              onChange={set("branch_id")}
            >
              <option value="">Not sure yet</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.branch_name}
                </option>
              ))}
            </select>
            <span style={s.err} />
          </div>

          <div style={s.field}>
            <label style={s.label} htmlFor="cf-grade">
              Year level
            </label>
            <select
              id="cf-grade"
              style={s.input}
              value={values.grade_id}
              onChange={set("grade_id")}
            >
              <option value="">Select a year</option>
              {grades.map((g) => (
                <option key={g.id} value={g.id}>
                  Year {g.name}
                </option>
              ))}
            </select>
            <span style={s.err} />
          </div>

          <div style={{ ...s.field, ...s.full }}>
            <label style={s.label} htmlFor="cf-message">
              How can we help? *
            </label>
            <textarea
              id="cf-message"
              style={s.textarea}
              value={values.message}
              onChange={set("message")}
              placeholder="Tell us a little about your child and what you are looking for."
            />
            <span style={s.err}>{errors.message}</span>
          </div>
        </div>

        {failed ? <div style={s.failed}>{failed}</div> : null}

        <div style={s.actions}>
          <button
            type="submit"
            disabled={sending}
            style={{ ...s.submit, ...(sending ? s.submitOff : null) }}
          >
            {sending ? "Sending…" : "Send enquiry"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ContactForm;
